import React, { useEffect, useRef, useState } from "react";
import { Rocket, Cpu, LayoutDashboard, Sparkles, Loader2 } from "lucide-react";

type PostLoginLoadingProps = {
  readyToComplete: boolean;
  onComplete?: () => void;
  companyName?: string;
  message?: string;
};

const MIN_LOADER_MS = 2000;

const PostLoginLoading: React.FC<PostLoginLoadingProps> = ({
  readyToComplete,
  onComplete,
  message = "AI & ML–powered insights flowing to your workspace. One moment while we connect your services.",
}) => {
  const onCompleteRef = useRef(onComplete);
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Enforce minimum 2 seconds of loader.
  useEffect(() => {
    const t = window.setTimeout(() => setMinTimeElapsed(true), MIN_LOADER_MS);
    return () => window.clearTimeout(t);
  }, []);

  // Complete only when both: minimum time done AND dashboard data ready.
  useEffect(() => {
    if (!minTimeElapsed || !readyToComplete) return;
    const t = window.setTimeout(() => {
      onCompleteRef.current?.();
    }, 300);
    return () => window.clearTimeout(t);
  }, [minTimeElapsed, readyToComplete]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-50 dark:bg-slate-950 overflow-hidden">
      <div
        className="absolute inset-0 opacity-40 dark:opacity-30"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(16, 185, 129, 0.15), transparent 60%), radial-gradient(ellipse 60% 40% at 80% 80%, rgba(99, 102, 241, 0.12), transparent 50%), radial-gradient(ellipse 50% 30% at 20% 70%, rgba(14, 165, 233, 0.1), transparent 45%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage: `linear-gradient(rgba(16, 185, 129, 0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(16, 185, 129, 0.5) 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative flex flex-col items-center gap-8 px-6 max-w-lg w-full">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20 border border-emerald-500/20 dark:border-emerald-500/30 p-4 animate-pulse">
            <Rocket className="size-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white tracking-tight">
            Preparing your dashboard
          </h1>
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 leading-relaxed max-w-md">
            {message}
          </p>
        </div>

        <div className="relative w-full flex items-center px-2 sm:px-4 gap-0">
          <div className="flex flex-col items-center gap-2 shrink-0 z-10 w-20 sm:w-24">
            <div className="flex items-center justify-center size-14 sm:size-16 rounded-2xl bg-violet-500/15 dark:bg-violet-500/25 border border-violet-500/30 dark:border-violet-500/40 shadow-lg shadow-violet-500/10">
              <Cpu className="size-7 sm:size-8 text-violet-600 dark:text-violet-400" />
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wider text-center">
              AI / Data
            </span>
          </div>

          <div className="flex-1 relative h-24 min-w-[100px] flex items-center">
            <div className="absolute inset-0 flex items-center px-2" aria-hidden>
              <div
                className="h-0.5 w-full rounded-full opacity-30 dark:opacity-40"
                style={{
                  background:
                    "repeating-linear-gradient(90deg, rgb(16 185 129 / 0.6), rgb(16 185 129 / 0.6) 6px, transparent 6px, transparent 12px)",
                }}
              />
            </div>
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center post-login-rocket-once">
              <div className="flex items-center justify-center size-11 sm:size-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 dark:from-emerald-500 dark:to-emerald-700 border border-emerald-400/50 dark:border-emerald-500/50 shadow-lg shadow-emerald-500/30 rotate-90">
                <Rocket className="size-5 sm:size-6 text-white -rotate-90" />
              </div>
              <div className="mt-0.5 flex gap-0.5">
                <span className="w-1 h-2.5 rounded-full bg-amber-400/80 post-login-flame" />
                <span className="w-1 h-3 rounded-full bg-orange-400/90 post-login-flame" style={{ animationDelay: "0.05s" }} />
                <span className="w-1 h-2.5 rounded-full bg-amber-400/80 post-login-flame" style={{ animationDelay: "0.1s" }} />
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 shrink-0 z-10 w-20 sm:w-24">
            <div className="flex items-center justify-center size-14 sm:size-16 rounded-2xl bg-emerald-500/15 dark:bg-emerald-500/25 border border-emerald-500/30 dark:border-emerald-500/40 shadow-lg shadow-emerald-500/10">
              <LayoutDashboard className="size-7 sm:size-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-slate-600 dark:text-slate-300 uppercase tracking-wider text-center">
              Dashboard
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-slate-400 dark:text-slate-500">
          <Sparkles className="size-4 animate-[pulse_1.2s_ease-in-out_infinite]" />
          <span className="text-xs">Service-based intelligence</span>
          <Sparkles className="size-4 animate-[pulse_1.2s_ease-in-out_infinite_0.6s]" />
        </div>
      </div>

      <style>{`
        @keyframes post-login-rocket-once-keyframes {
          0% { left: 0%; }
          100% { left: 100%; }
        }
        .post-login-rocket-once {
          left: 0%;
          animation: post-login-rocket-once-keyframes ${MIN_LOADER_MS}ms ease-out forwards;
        }
        @keyframes post-login-flame {
          0%, 100% { transform: scaleY(0.85); opacity: 0.85; }
          50% { transform: scaleY(1.15); opacity: 1; }
        }
        .post-login-flame {
          animation: post-login-flame 0.35s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default PostLoginLoading;
